/**
 * AI Chat Redux Actions
 *
 * This module defines all Redux actions for AI Chat feature including:
 * - Chat lifecycle (open/close)
 * - UI state management (panel widths, FAB positions)
 * - Resize operations
 *
 * See documentation:
 * - Redux docs: /wiki/redux/ai-chat.md
 * - Feature docs: /wiki/features/ai-chat/open-close-ai-chat.md
 */

// ============================================================================
// Action Type Constants
// ============================================================================

// Chat Lifecycle Actions
export const OPEN_AI_CHAT = 'AI_CHAT_OPEN';
export const CLOSE_AI_CHAT = 'AI_CHAT_CLOSE';
export const START_NEW_CHAT = 'AI_CHAT_START_NEW_CHAT';
export const START_NEW_CHAT_SESSION = 'AI_CHAT_START_NEW_CHAT_SESSION';
export const START_NEW_CHAT_USING_QUICK_ACTION = 'AI_CHAT_START_NEW_CHAT_USING_QUICK_ACTION';
export const START_NEW_CHAT_USING_QUICK_ACTION_BY_NAME = 'AI_CHAT_START_NEW_CHAT_USING_QUICK_ACTION_BY_NAME';
export const OPEN_EXISTING_CHAT = 'AI_CHAT_OPEN_EXISTING_CHAT';
export const SET_CURRENT_CHAT_ID = 'AI_CHAT_SET_CURRENT_CHAT_ID';
export const SHOW_CHAT_HISTORY = 'AI_CHAT_SHOW_CHAT_HISTORY';
export const HIDE_CHAT_HISTORY = 'AI_CHAT_HIDE_CHAT_HISTORY';

// Message Operations Actions
export const SEND_MESSAGE = 'AI_CHAT_SEND_MESSAGE';
export const CANCEL_MESSAGE = 'AI_CHAT_CANCEL_MESSAGE';
export const CANCEL_MESSAGE_SUCCESS = 'AI_CHAT_CANCEL_MESSAGE_SUCCESS';
export const CANCEL_MESSAGE_FAILED = 'AI_CHAT_CANCEL_MESSAGE_FAILED';
export const EDIT_MESSAGE = 'AI_CHAT_EDIT_MESSAGE';
export const SET_EDIT_MESSAGE = 'AI_CHAT_SET_EDIT_MESSAGE';
export const SET_EDIT_DRAFT = 'AI_CHAT_SET_EDIT_DRAFT';
export const REGENERATE_RESPONSE = 'AI_CHAT_REGENERATE_RESPONSE';
export const COPY_MESSAGE = 'AI_CHAT_COPY_MESSAGE';
export const ADD_REACTION = 'AI_CHAT_ADD_REACTION';
export const REMOVE_REACTION = 'AI_CHAT_REMOVE_REACTION';

// Chat Management Actions
export const PIN_CHAT = 'AI_CHAT_PIN_CHAT';
export const RENAME = 'AI_CHAT_RENAME';
export const DELETE = 'AI_CHAT_DELETE';
export const SHARE_CHAT = 'AI_CHAT_SHARE_CHAT';
export const FORK_CHAT = 'AI_CHAT_FORK_CHAT';


// UI State Actions
export const UPDATE_AI_CHAT_WIDTH = 'AI_CHAT_UPDATE_WIDTH';
export const UPDATE_CARD_DETAIL_WIDTH = 'AI_CHAT_UPDATE_CARD_DETAIL_WIDTH';
export const UPDATE_FAB_POSITION = 'AI_CHAT_UPDATE_FAB_POSITION';
export const RESIZE_PANEL_START = 'AI_CHAT_RESIZE_PANEL_START';
export const RESIZE_PANEL_END = 'AI_CHAT_RESIZE_PANEL_END';
export const UPDATE_DRAFT_MESSAGE = 'AI_CHAT_UPDATE_DRAFT_MESSAGE';
export const CLEAR_DRAFT_MESSAGE = 'AI_CHAT_CLEAR_DRAFT_MESSAGE';
export const CLEAR_NEW_CHAT_DRAFT = 'AI_CHAT_CLEAR_NEW_CHAT_DRAFT';

// Internal Draft Message Actions (dispatched by saga, uses contextType/boardId/cardId — key built in reducer)
export const SET_DRAFT_MESSAGE = 'AI_CHAT_SET_DRAFT_MESSAGE';
export const SET_NEW_CHAT_DRAFT = 'AI_CHAT_SET_NEW_CHAT_DRAFT';

// Strips upload chips by ID from every persisted draft. Dispatched from
// the ai-chat-attachments pagehide handler when the user confirms a reload.
export const REMOVE_DRAFT_CHIPS_BY_IDS = 'AI_CHAT_REMOVE_DRAFT_CHIPS_BY_IDS';

// New Chat ID persistence — pre-generated chatId for new chats with file attachments
export const SET_NEW_CHAT_ID = 'AI_CHAT_SET_NEW_CHAT_ID';
export const CLEAR_NEW_CHAT_ID = 'AI_CHAT_CLEAR_NEW_CHAT_ID';

// Auto-submit pending state — set by saga when send is clicked while uploads are in progress
export const SET_AUTO_SUBMIT = 'AI_CHAT_SET_AUTO_SUBMIT';
export const CLEAR_AUTO_SUBMIT = 'AI_CHAT_CLEAR_AUTO_SUBMIT';

// Internal Board Creation Tracking (dispatched by saga)
export const MARK_BOARD_CREATION_DONE = 'AI_CHAT_MARK_BOARD_CREATION_DONE';

// Internal Chat Load Status (dispatched by saga)
export const SET_CHAT_ACCESS_DENIED = 'AI_CHAT_SET_CHAT_ACCESS_DENIED';

// AI Edit — Approve actions
export const APPROVE_TOOL_CALLS = 'AI_CHAT_APPROVE_TOOL_CALLS';
export const APPROVE_CARD_TOOL_CALLS = 'AI_CHAT_APPROVE_CARD_TOOL_CALLS';

// AI Edit — Reject actions
export const UPDATE_TOOL_CALL_INPUT = 'AI_CHAT_UPDATE_TOOL_CALL_INPUT';
export const TOOL_CALL_INPUT_FAILED = 'AI_CHAT_TOOL_CALL_INPUT_FAILED';
export const REJECT_TOOL_CALLS = 'AI_CHAT_REJECT_TOOL_CALLS';
export const REJECT_CARD_TOOL_CALLS = 'AI_CHAT_REJECT_CARD_TOOL_CALLS';

// AI Edit — Undo-reject action (saga is a separate dev-list task)
export const UNDO_REJECT_TOOL_CALLS = 'AI_CHAT_UNDO_REJECT_TOOL_CALLS';

// AI Edit — Cross-panel hover sync (panel → board card highlight)
export const UPDATE_HOVER_CARD = 'AI_CHAT_UPDATE_HOVER_CARD';

/**
 * Marks a board creation as auto-navigated so it won't trigger again.
 *
 * @param {String} chatId - Chat ID
 * @param {String} toolMessageId - Tool message document ID
 * @returns {Object} Redux action
 */
export const markBoardCreationDone = (chatId, toolMessageId) => {
  return {
    type: MARK_BOARD_CREATION_DONE,
    key: `${chatId}_${toolMessageId}`
  };
};

// ============================================================================
// Chat Lifecycle Action Creators
// ============================================================================

/**
 * Opens AI Chat panel with the specified context.
 *
 * The AI Chat panel can be opened from two contexts:
 * - BOARD: AI Chat opens as a side panel alongside the board (desktop) or bottom sheet (mobile)
 * - CARD: AI Chat opens in split-view with card detail (desktop) or bottom sheet (mobile)
 *
 * Layout mode (SIDE_PANEL vs BOTTOM_SHEET) is determined by selector based on:
 * - Device type (mobile, tablet, desktop)
 * - Screen orientation (portrait, landscape)
 * - Current context (board or card)
 * - Viewport width
 *
 * @param {String} contextType - Context type: 'BOARD' or 'CARD'
 * @param {String} boardId - ID of the current board
 * @param {String} [cardId] - ID of the current card (required when contextType is 'CARD')
 * @returns {Object} Redux action
 *
 * @example
 * // Open AI Chat from board
 * dispatch(openAiChat('BOARD', 'brd_5QH0NJNkid1ljr18TM1Tdl'));
 *
 * @example
 * // Open AI Chat from card detail
 * dispatch(openAiChat('CARD', 'brd_5QH0NJNkid1ljr18TM1Tdl', 'crd_77BnCMFhXtp6NvS1hrtB6u'));
 */
export const openAiChat = (contextType, boardId, cardId) => {
  return {
    type: OPEN_AI_CHAT,
    contextType,
    boardId,
    cardId
  };
};

/**
 * Closes AI Chat panel.
 *
 * This action:
 * - Sets isOpen to false
 * - Clears currentContext
 * - Restores layout to pre-chat state:
 *   - SIDE_PANEL mode: Board restores to full width, card detail restores to remembered width
 *   - BOTTOM_SHEET mode: Modal dialog closes
 *
 * Panel widths and FAB positions are preserved in persisted state for next session.
 *
 * @returns {Object} Redux action
 *
 * @example
 * // Close AI Chat from header close button
 * dispatch(closeAiChat());
 */
export const closeAiChat = () => {
  return {
    type: CLOSE_AI_CHAT
  };
};

/**
 * Starts a new AI Chat using a quick action by ID.
 *
 * Used by the quick action item component when user clicks a quick action button.
 * The component passes the quick action ID and context directly.
 *
 * @param {String} quickActionId - ID of the quick action (e.g., 'aiqa_xxx')
 * @param {Object} context - Context object
 * @param {String} context.type - Context type: 'BOARD' | 'CARD' | 'HOME' | 'ONBOARDING'
 * @param {String} [context.boardId] - Board ID
 * @param {String} [context.cardId] - Card ID (optional, for card context)
 * @returns {Object} Redux action
 */
export const startNewChatUsingQuickAction = (quickActionId, context) => {
  return {
    type: START_NEW_CHAT_USING_QUICK_ACTION,
    quickActionId,
    context
  };
};

/**
 * Starts a new AI Chat using a quick action by name.
 *
 * This action validates the quick action availability, creates a new chat,
 * and opens the AI Chat panel with the quick action's initial prompt.
 *
 * Quick actions are predefined AI prompts (e.g., 'CREATE_BOARD', 'MAKE_BOARD_LIKE_THIS')
 * stored in Firestore. The saga will:
 * 1. Query Firestore for the quick action by name where disabled = false
 * 2. Show toast error if quick action is unavailable or disabled
 * 3. Call POST /ai/chats API with context and quick action data
 * 4. Open AI Chat panel with the new chat
 *
 * @param {String} quickActionName - Name of the quick action (e.g., 'MAKE_BOARD_LIKE_THIS', 'CREATE_BOARD', 'CREATE_TEMPLATE')
 * @param {Object} context - Context object
 * @param {String} context.type - Context type: 'BOARD' | 'HOME' | 'ONBOARDING'
 * @param {String} [context.boardId] - Board ID (required when type is 'BOARD')
 * @param {String} [context.cardId] - Card ID (optional, for card context)
 * @returns {Object} Redux action
 *
 * @example
 * // Start "Make a board like this" from board page
 * dispatch(startAiChatWithQuickActionByName('MAKE_BOARD_LIKE_THIS', { type: 'BOARD', boardId: 'brd_123' }));
 *
 * @example
 * // Start "Create board" from Explorer page
 * dispatch(startAiChatWithQuickActionByName('CREATE_BOARD', { type: 'HOME' }));
 *
 * @example
 * // Start "Create board" from onboarding flow
 * dispatch(startAiChatWithQuickActionByName('CREATE_BOARD', { type: 'ONBOARDING' }));
 */
export const startAiChatWithQuickActionByName = (quickActionName, context, chatAttachments) => {
  return {
    type: START_NEW_CHAT_USING_QUICK_ACTION_BY_NAME,
    quickActionName,
    context,
    chatAttachments, // Optional: { chatId, attachmentIds } — pre-uploaded files from import dialog
  };
};

// ============================================================================
// UI State Action Creators
// ============================================================================

/**
 * Updates AI chat panel width.
 *
 * This action is dispatched when user resizes AI chat panel via resize handle.
 * Width is persisted separately for BOARD and CARD contexts via persistEnhancer.
 *
 * Resize constraints:
 * - Minimum width: 320px
 * - Maximum width: 60% of viewport width
 * - Falls back to default if viewport too small
 *
 * In card detail split-view:
 * - Dragging right edge resizes AI chat panel
 * - Card detail panel adjusts accordingly to maintain combined width
 *
 * @param {Number} width - New width in pixels (minimum: 320px, maximum: 60% of viewport)
 * @param {String} context - Context type: 'BOARD' or 'CARD'
 * @returns {Object} Redux action
 *
 * @example
 * // Update board context AI chat width to 400px
 * dispatch(updateAiChatWidth(400, 'BOARD'));
 *
 * @example
 * // Update card context AI chat width to 380px
 * dispatch(updateAiChatWidth(380, 'CARD'));
 */
export const updateAiChatWidth = (width, context) => {
  return {
    type: UPDATE_AI_CHAT_WIDTH,
    width,
    context
  };
};

/**
 * Updates the total card detail dialog width.
 *
 * This action is dispatched when user resizes the card detail dialog (left/right
 * edge drag) or when the center divider is dragged to redistribute space between
 * card detail content and AI chat panel.
 *
 * The stored value is always the total dialog width. When AI chat is open, the
 * card detail content width = total - aiChatPanelWidth.
 *
 * Resize constraints:
 * - Minimum total width (chat closed): 660px
 * - Minimum total width (chat open): 720px (400 card detail + 320 AI chat)
 * - Maximum width: Viewport width minus peripheral margins (48px)
 *
 * @param {Number} width - New total dialog width in pixels
 * @returns {Object} Redux action
 *
 * @example
 * // Update total dialog width to 1200px
 * dispatch(updateCardDetailWidth(1200));
 */
export const updateCardDetailWidth = (width) => {
  return {
    type: UPDATE_CARD_DETAIL_WIDTH,
    width,
  };
};

/**
 * Updates FAB (Floating Action Button) position.
 *
 * This action is dispatched when user drags FAB to a new position.
 * Position is persisted separately for BOARD and CARD contexts via persistEnhancer.
 *
 * FAB behavior:
 * - Draggable within board/card boundaries
 * - Position is relative to board/card container, not viewport
 * - Position is persisted per device separately for board and card
 * - Chat panel always opens from default position (bottom-right), NOT from dragged FAB position
 *
 * @param {Object} position - Position object with x and y coordinates
 * @param {Number} position.x - X coordinate in pixels (relative to container)
 * @param {Number} position.y - Y coordinate in pixels (relative to container)
 * @param {String} context - Context type: 'BOARD' or 'CARD'
 * @returns {Object} Redux action
 *
 * @example
 * // Update FAB position in board context
 * dispatch(updateFabPosition({ x: 20, y: 20 }, 'BOARD'));
 *
 * @example
 * // Update FAB position in card context after drag (y is from the top edge)
 * dispatch(updateFabPosition({ x: 60, y: 20 }, 'CARD'));
 */
export const updateFabPosition = (position, context) => {
  return {
    type: UPDATE_FAB_POSITION,
    position,
    context
  };
};

/**
 * Marks the start of a panel resize operation.
 *
 * This action is dispatched when user starts dragging a resize handle.
 * Updates UI state to reflect active resize operation:
 * - Sets isResizing to true
 * - Can be used to show resize cursors
 * - Can be used to disable CSS transitions during resize for better performance
 *
 * @returns {Object} Redux action
 *
 * @example
 * // When user starts dragging resize handle
 * const handleMouseDown = () => {
 *   dispatch(resizePanelStart());
 * };
 */
export const resizePanelStart = () => {
  return {
    type: RESIZE_PANEL_START
  };
};

/**
 * Marks the end of a panel resize operation.
 *
 * This action is dispatched when user releases a resize handle.
 * Updates UI state and triggers persistence:
 * - Sets isResizing to false
 * - Triggers persistence of panel widths to persistEnhancer
 * - Re-enables CSS transitions
 *
 * @returns {Object} Redux action
 *
 * @example
 * // When user releases resize handle
 * const handleMouseUp = () => {
 *   dispatch(resizePanelEnd());
 * };
 */
export const resizePanelEnd = () => {
  return {
    type: RESIZE_PANEL_END
  };
};

// ============================================================================
// Chat Conversation Action Creators
// ============================================================================

/**
 * Creates a new AI chat with initial message.
 *
 * This action can be used for both quick actions and typed messages:
 * - Quick action flow: Provide request.quickAction with { id, title, groupName }
 * - Typed message flow: Provide request.userPrompt
 *
 * The saga will call the server API to create the chat and first message,
 * then update Redux state with the new chatId and messageId.
 *
 * @param {Object} context - Context object { type, boardId, cardId? }
 * @param {Object} request - Request object { userPrompt?, quickAction?, context?, references?, forkedFrom? }
 * @returns {Object} Redux action
 *
 * @example
 * // Start new chat with typed message
 * dispatch(startNewChat({ type: 'BOARD', boardId: 'brd_123' }, { userPrompt: 'How can I improve this board?', context: {}, references: [] }));
 *
 * @example
 * // Start new chat with quick action
 * dispatch(startNewChat({ type: 'CARD', boardId: 'brd_123', cardId: 'crd_456' }, { quickAction: { id: 'aiqa_summarize', title: 'Summarize', groupName: 'Analysis' }, context: {}, references: [] }));
 */
export const startNewChat = (context, request) => {
  return {
    type: START_NEW_CHAT,
    context,
    request
  };
};

/**
 * Starts a new chat session without creating a chat yet.
 *
 * This action clears the current chat context and shows the welcome message.
 * The actual chat is created only when the user sends their first message.
 *
 * @returns {Object} Redux action
 *
 * @example
 * // User clicks "Start new chat" button
 * dispatch(startNewChatSession());
 */
export const startNewChatSession = () => {
  return {
    type: START_NEW_CHAT_SESSION
  };
};

/**
 * Opens an existing chat from history.
 *
 * This action sets the currentChatId and loads messages from Firestore.
 * The saga will update the URL hash to reflect the opened chat.
 *
 * @param {String} chatId - ID of the chat to open
 * @returns {Object} Redux action
 *
 * @example
 * // User clicks on a chat in history
 * dispatch(openExistingChat('aic_5QH0NJNkid1ljr18TM1Tdl'));
 */
export const openExistingChat = (chatId) => {
  return {
    type: OPEN_EXISTING_CHAT,
    chatId
  };
};

/**
 * Sets the current chat ID.
 *
 * This is an internal action used by sagas to update the current chat ID.
 *
 * @param {String} chatId - ID of the current chat
 * @returns {Object} Redux action
 */
export const setCurrentChatId = (chatId) => {
  return {
    type: SET_CURRENT_CHAT_ID,
    chatId
  };
};

/**
 * Shows the chat history list view.
 *
 * @returns {Object} Redux action
 *
 * @example
 * // User clicks "View history" button
 * dispatch(showChatHistory());
 */
export const showChatHistory = () => {
  return {
    type: SHOW_CHAT_HISTORY
  };
};

/**
 * Hides the chat history list view, returning to the current chat.
 *
 * @returns {Object} Redux action
 *
 * @example
 * // User clicks the back button in the history header
 * dispatch(hideChatHistory());
 */
export const hideChatHistory = () => {
  return {
    type: HIDE_CHAT_HISTORY
  };
};

// ============================================================================
// Message Operations Action Creators
// ============================================================================

/**
 * Sends a new message or submits an edited message.
 *
 * The saga will check if there's an editing message ID for the given chatId:
 * - If editing: Calls edit API and creates a new message
 * - If not editing: Calls send API to create a new follow-up message
 *
 * @param {String} chatId - ID of the current chat
 * @param {Object} request - Request object { userPrompt, context?, references? }
 * @returns {Object} Redux action
 *
 * @example
 * // Send follow-up message
 * dispatch(sendMessage('aic_123', { userPrompt: 'Tell me more about this', context: {}, references: [] }));
 */
export const sendMessage = (chatId, request) => {
  return {
    type: SEND_MESSAGE,
    chatId,
    request
  };
};

/**
 * Cancels an in-progress message generation.
 *
 * @param {String} chatId - ID of the current chat
 * @param {String} messageId - ID of the message to cancel
 * @returns {Object} Redux action
 *
 * @example
 * // Cancel current message generation
 * dispatch(cancelMessage('aic_123', 'aim_456'));
 */
export const cancelMessage = (chatId, messageId) => {
  return {
    type: CANCEL_MESSAGE,
    chatId,
    messageId
  };
};

/**
 * Initiates editing of a message.
 *
 * This action sets the editing state and pre-fills the input with the message text.
 *
 * @param {String} chatId - ID of the current chat
 * @param {String} messageId - ID of the message to edit
 * @returns {Object} Redux action
 *
 * @example
 * // User clicks edit on a message
 * dispatch(editMessage('aic_123', 'aim_456'));
 */
export const editMessage = (chatId, messageId) => {
  return {
    type: EDIT_MESSAGE,
    chatId,
    messageId
  };
};

/**
 * Sets or clears the editing message state.
 *
 * This is an internal action used by sagas to track which message is being edited.
 *
 * @param {String} chatId - ID of the current chat
 * @param {String|null} messageId - ID of the message being edited (null to exit edit mode)
 * @returns {Object} Redux action
 */
export const setEditMessage = (chatId, messageId) => {
  return {
    type: SET_EDIT_MESSAGE,
    chatId,
    messageId
  };
};

/**
 * Sets the in-progress edit text for a chat's currently-edited message, so it survives a
 * panel close/reopen (and page reload). Kept separate from the unsent draft; cleared when
 * edit mode ends (see the SET_EDIT_MESSAGE reducer).
 *
 * @param {String} chatId - ID of the current chat
 * @param {String} draftText - The edited text so far
 * @returns {Object} Redux action
 */
export const setEditDraft = (chatId, draftText) => {
  return {
    type: SET_EDIT_DRAFT,
    chatId,
    draftText
  };
};

/**
 * Regenerates the AI response for a message.
 *
 * @param {String} chatId - ID of the current chat
 * @param {String} messageId - ID of the message to regenerate
 * @returns {Object} Redux action
 *
 * @example
 * // User clicks regenerate button
 * dispatch(regenerateResponse('aic_123', 'aim_456'));
 */
export const regenerateResponse = (chatId, messageId) => {
  return {
    type: REGENERATE_RESPONSE,
    chatId,
    messageId
  };
};

/**
 * Adds or updates a reaction (LIKE or DISLIKE) for a specific response.
 *
 * @param {String} chatId - ID of the current chat
 * @param {String} messageId - ID of the message
 * @param {String} responseId - ID of the response
 * @param {String} reactionType - Reaction type: 'LIKE' or 'DISLIKE'
 * @returns {Object} Redux action
 *
 * @example
 * // Like a response
 * dispatch(addReaction('aic_123', 'aim_456', 'resp_1', 'LIKE'));
 */
export const addReaction = (chatId, messageId, responseId, reactionType) => {
  return {
    type: ADD_REACTION,
    chatId,
    messageId,
    responseId,
    reactionType
  };
};

/**
 * Removes a reaction from a response.
 *
 * @param {String} chatId - ID of the current chat
 * @param {String} messageId - ID of the message
 * @param {String} responseId - ID of the response
 * @returns {Object} Redux action
 *
 * @example
 * // Remove reaction
 * dispatch(removeReaction('aic_123', 'aim_456', 'resp_1'));
 */
export const removeReaction = (chatId, messageId, responseId) => {
  return {
    type: REMOVE_REACTION,
    chatId,
    messageId,
    responseId
  };
};

/**
 * Copies message text to system clipboard.
 *
 * For REQUEST messages: Copies `request.userPrompt` text.
 * For RESPONSE messages: Copies `response.content` (preserves Markdown formatting).
 *
 * @param {String} messageId - ID of the AIMessage
 * @param {String} messageType - Type of message: 'REQUEST' or 'RESPONSE'
 * @param {String} [responseId] - Required when messageType is 'RESPONSE'. Specifies which response from responses[] array to copy.
 * @returns {Object} Redux action
 *
 * @example
 * // Copy request message
 * dispatch(copyMessage('aicm_123', 'REQUEST'));
 *
 * @example
 * // Copy specific response message
 * dispatch(copyMessage('aicm_123', 'RESPONSE', 'resp_1'));
 */
export const copyMessage = (messageId, messageType, responseId) => {
  return {
    type: COPY_MESSAGE,
    messageId,
    messageType,
    responseId
  };
};

// ============================================================================
// Draft Message Action Creators
// ============================================================================

/**
 * Updates draft message text.
 *
 * This is a unified action that handles both new chat drafts and existing chat drafts:
 * - If chatId is provided: Updates draft for existing chat
 * - If chatId is null/undefined: Updates draft for new chat based on current context
 *
 * The saga will automatically persist the draft via persistEnhancer.
 *
 * @param {String} draftText - Current draft message text
 * @param {String|null} chatId - Chat ID (null for new chats)
 * @returns {Object} Redux action
 *
 * @example
 * // Update draft for existing chat
 * dispatch(updateDraftMessage('Draft text...', 'aic_123'));
 *
 * @example
 * // Update draft for new chat
 * dispatch(updateDraftMessage('Draft text...', null));
 */
export const updateDraftMessage = (draftText, chatId) => {
  return {
    type: UPDATE_DRAFT_MESSAGE,
    draftText,
    chatId
  };
};

/**
 * Clears draft message for an existing chat.
 *
 * @param {String} chatId - Chat ID
 * @param {String} userId - User ID (required to scope draft to the correct user)
 * @returns {Object} Redux action
 *
 * @example
 * // Clear draft after sending message
 * dispatch(clearDraftMessage('aic_123', 'usr_abc'));
 */
export const clearDraftMessage = (chatId, userId) => {
  return {
    type: CLEAR_DRAFT_MESSAGE,
    chatId,
    userId
  };
};

/**
 * Clears draft message for a new chat.
 *
 * @param {String} contextType - Context type: 'BOARD' or 'CARD'
 * @param {String} boardId - Board ID
 * @param {String} cardId - Card ID (for CARD context)
 * @param {String} userId - User ID (required to scope draft to the correct user)
 * @returns {Object} Redux action
 *
 * @example
 * // Clear new chat draft after sending first message
 * dispatch(clearNewChatDraft('BOARD', 'brd_123', undefined, 'usr_abc'));
 */
export const clearNewChatDraft = (contextType, boardId, cardId, userId) => {
  return {
    type: CLEAR_NEW_CHAT_DRAFT,
    contextType,
    boardId,
    cardId,
    userId
  };
};

/**
 * Strips upload chips by ID from every persisted draft. Used by the
 * ai-chat-attachments pagehide handler to remove orphan upload chips before
 * the page reloads.
 *
 * @param {String[]} attachmentIds The `item-id` values to remove.
 */
export const removeDraftChipsByIds = (attachmentIds) => ({
  type: REMOVE_DRAFT_CHIPS_BY_IDS,
  attachmentIds,
});

/**
 * Stores a pre-generated chatId for a new chat that has file attachments.
 * Called from ai-chat-input.attachFiles() when no chatId exists yet.
 * Persisted via persistEnhancer so the same chatId is used after page refresh.
 *
 * @param {String} contextType - Context type: 'BOARD', 'CARD', 'HOME', or 'ONBOARDING'
 * @param {String} boardId - Board ID (for BOARD/CARD contexts)
 * @param {String} cardId - Card ID (for CARD context)
 * @param {String} chatId - Pre-generated chatId (aic_xxx)
 */
export const setNewChatId = (contextType, boardId, cardId, chatId) => {
  return {
    type: SET_NEW_CHAT_ID,
    contextType,
    boardId,
    cardId,
    chatId
  };
};

/**
 * Clears the pre-generated chatId after the first message is sent.
 * Called from ai-chat-panel._handleSend() after dispatching startNewChat.
 *
 * @param {String} contextType - Context type: 'BOARD', 'CARD', 'HOME', or 'ONBOARDING'
 * @param {String} boardId - Board ID (for BOARD/CARD contexts)
 * @param {String} cardId - Card ID (for CARD context)
 */
export const clearNewChatId = (contextType, boardId, cardId) => {
  return {
    type: CLEAR_NEW_CHAT_ID,
    contextType,
    boardId,
    cardId
  };
};

/**
 * Internal — dispatched by saga to enter auto-submit pending state.
 * @param {String} contextKey - e.g. 'board_brd_123' or 'aic_456'
 */
export const _setAutoSubmit = (contextKey) => ({
  type: SET_AUTO_SUBMIT,
  contextKey,
});

/**
 * Clears auto-submit state. Dispatched by saga on completion and by the Stop button to cancel.
 * @param {String} contextKey
 */
export const clearAutoSubmit = (contextKey) => ({
  type: CLEAR_AUTO_SUBMIT,
  contextKey,
});

export const VIEW_AI_CHAT = 'AI_CHAT_VIEW_AI_CHAT';

/**
 * Navigates to the source board and opens the given chat there. When the chat is a card's own
 * chat, `cardId` is passed so the card is opened and its chat panel — not the board panel — is
 * the one that opens.
 *
 * Dispatched by ai-chat-fab-button's [View AI Chat] button. Phase 2 also dispatches this from
 * other-board group-header links in the AI Helper checklist.
 *
 * @param {String} chatId - ID of the chat to open
 * @param {String} sourceBoardId - Board where the chat is running
 * @param {String} [cardId] - Card the chat belongs to, for a card-context chat
 * @returns {Object} Redux action
 */
export const viewAiChat = (chatId, sourceBoardId, cardId) => ({
  type: VIEW_AI_CHAT,
  chatId,
  sourceBoardId,
  cardId,
});

 /**
 * Shares or unshares a chat conversation.
 *
 * The saga calls the server API to update the chat's shared status.
 * On success, constructs the shared chat link and copies it to clipboard,
 * then shows a success notification.
 *
 * @param {String} chatId - ID of the chat to share
 * @param {String|null} [messageId=null] - Optional message ID for deep linking (not sent to server API)
 * @param {Boolean} [shared=true] - Whether to share (true) or unshare (false)
 * @returns {Object} Redux action
 *
 * @example
 * // Share entire conversation from header
 * dispatch(shareChat('aic_123'));
 *
 * @example
 * // Share conversation with deep link to specific message
 * dispatch(shareChat('aic_123', 'aicm_456'));
 */
/**
 * Renames a chat.
 *
 * The saga writes the new title onto the chat document as a local write, calls the server, and puts
 * the previous title back if that call fails.
 *
 * @param {Object} args
 *  @property {String} chatId - ID of the chat to rename
 *  @property {String} title - New title
 * @returns {Object} Redux action
 *
 * @example
 * dispatch(rename({ chatId: 'aic_123', title: 'Sprint planning' }));
 */
export const rename = ({ chatId, title }) => {
  return {
    type: RENAME,
    chatId,
    title
  };
};

/**
 * Deletes a chat.
 *
 * The saga removes the chat document as a local write, calls the server, and puts it back if that
 * call fails. When the chat removed is the one open, a new chat is left open in its place.
 *
 * @param {Object} args
 *  @property {String} chatId - ID of the chat to delete
 * @returns {Object} Redux action
 *
 * @example
 * dispatch(del({ chatId: 'aic_123' }));
 */
export const del = ({ chatId }) => {
  return {
    type: DELETE,
    chatId
  };
};

export const shareChat = (chatId, messageId = null, shared = true) => {
  return {
    type: SHARE_CHAT,
    chatId,
    messageId,
    shared
  };
};

/**
 * Approves one or more AI-proposed tool calls within a response.
 *
 * Triggers `approveToolCallsSaga`, which POSTs to
 * `/ai/chats/:chatId/messages/:messageId/responses/:responseId/tool-calls/accept`
 * with `{ toolCallIds }` (chatId/messageId derived from the tool-message doc). The
 * server always returns HTTP 200; per-id outcomes in the body are informational
 * only — authoritative state arrives through the Firestore subscription.
 *
 * @param {String} responseId - The AiResponse id that owns the tool calls
 * @param {String[]} toolCallIds - Tool-call ids to approve
 * @returns {Object} Redux action
 */
export const approveToolCalls = (responseId, toolCallIds) => ({
  type: APPROVE_TOOL_CALLS,
  responseId,
  toolCallIds,
});

/**
 * Approves every WAITING_FOR_APPROVAL leaf edit ToolCall on a single card,
 * across every response in the chat. Used by the group-level Approve button in
 * the AI Helper's edit-card row (and reused by the board card's Approve action).
 *
 * Triggers `approveCardToolCallsSaga`, which resolves the leaves and re-dispatches
 * `approveToolCalls(responseId, leafIds)` for each affected response.
 *
 * @param {String} chatId - Parent chat id (used to scope the leaf lookup)
 * @param {String} cardId - Target card id
 * @returns {Object} Redux action
 */
export const approveCardToolCalls = (chatId, cardId) => ({
  type: APPROVE_CARD_TOOL_CALLS,
  chatId,
  cardId,
});

/**
 * Rejects one or more AI-proposed tool calls within a response.
 *
 * Triggers `rejectToolCallsSaga`, which POSTs to
 * `/ai/chats/:chatId/messages/:messageId/responses/:responseId/tool-calls/reject`
 * with `{ toolCallIds }` (chatId/messageId derived from the tool-message doc). The
 * server always returns HTTP 200; per-id outcomes in the body are informational
 * only — authoritative state arrives through the Firestore subscription.
 *
 * @param {String} responseId - The AiResponse id that owns the tool calls
 * @param {String[]} toolCallIds - Tool-call ids to reject
 * @returns {Object} Redux action
 */
export const rejectToolCalls = (responseId, toolCallIds) => ({
  type: REJECT_TOOL_CALLS,
  responseId,
  toolCallIds,
});

/**
 * Saves the user's corrected version of a waiting proposal; with `shouldApprove`, approves it once the server holds it.
 * `updateToolCallInputSaga` PUTs the complete final input (not a diff) to `.../tool-calls/:toolCallId/input`.
 *
 * @param {String} responseId - The AiResponse id that owns the tool call
 * @param {String} toolCallId - The tool call being corrected
 * @param {Object} input - The complete final input
 * @param {Boolean} [shouldApprove] - Approve the proposal once the server holds this input
 * @param {String} [htmlField] - The key of `input` holding editor HTML (`text` or `description`), sent as Markdown
 * @returns {Object} Redux action
 */
export const updateToolCallInput = (responseId, toolCallId, input, shouldApprove = false, htmlField) => ({
  type: UPDATE_TOOL_CALL_INPUT,
  responseId,
  toolCallId,
  input,
  shouldApprove,
  htmlField,
});

/** The correction an approval was waiting on never reached the server, so the proposal is not approved after all. */
export const _toolCallInputFailed = (toolCallId) => ({
  type: TOOL_CALL_INPUT_FAILED,
  toolCallId,
});

/**
 * Rejects every WAITING_FOR_APPROVAL leaf edit ToolCall on a single card,
 * across every response in the chat. Counterpart of `approveCardToolCalls`.
 *
 * Triggers `rejectCardToolCallsSaga`, which resolves the leaves and re-dispatches
 * `rejectToolCalls(responseId, leafIds)` for each affected response.
 *
 * @param {String} chatId - Parent chat id (used to scope the leaf lookup)
 * @param {String} cardId - Target card id
 * @returns {Object} Redux action
 */
export const rejectCardToolCalls = (chatId, cardId) => ({
  type: REJECT_CARD_TOOL_CALLS,
  chatId,
  cardId,
});

/**
 * Reverses a previous rejection on one or more tool calls.
 *
 * Dispatched from the AI Helper row's undo icon (shown for REJECTED rows while
 * the response still has pending edits). The saga itself is implemented
 * in a separate dev-list task ("User undoes a reject from the AI Helper"). For
 * now the action is defined so the icon's click has a meaningful dispatch site.
 *
 * @param {String} responseId - The AiResponse id that owns the tool calls
 * @param {String[]} toolCallIds - Tool-call ids whose REJECTED status should
 *   be reverted back to WAITING_FOR_APPROVAL.
 * @returns {Object} Redux action
 */
export const undoRejectToolCalls = (responseId, toolCallIds) => ({
  type: UNDO_REJECT_TOOL_CALLS,
  responseId,
  toolCallIds,
});

/**
 * Cross-panel hover sync. Dispatched on @mouseenter / @mouseleave of a card
 * title in an AI Helper tool-call row. The matching <kerika-board-card> reads
 * this value via the `hoverCardId` selector and reflects an `[ai-hovered]`
 * host attribute so the board card visually highlights to match.
 *
 * Pass `null` to clear (mouseleave, panel disconnected, etc.).
 *
 * @param {String|null} cardId - Card id to highlight, or null to clear.
 * @returns {Object} Redux action
 */
export const updateHoverCard = (cardId) => ({
  type: UPDATE_HOVER_CARD,
  cardId: cardId || null,
});
